import React, { useEffect, useState } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { workHistory } from "../data";

const experienceVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.5,
      ease: "easeOut",
    },
  }),
};

const accordionVariants = {
  open: { 
    height: "auto",
    opacity: 1,
    transition: { 
      duration: 0.3,
      ease: "easeOut"
    }
  },
  closed: { 
    height: 0,
    opacity: 0,
    transition: { 
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

export default function WorkHistory() {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <motion.section
      id="work-history"
      ref={ref}
      initial="hidden"
      animate={controls}
      className="flex items-center justify-center bg-gray-800 text-white pt-16 pb-8 w-full px-4 sm:px-6 md:px-8"
    >
      <div className="w-full max-w-7xl container mx-auto p-5 md:p-20 flex flex-col items-start space-y-6 font-ntr">
        <h2 className="text-4xl mb-10 sm:mb-5 font-bold text-left w-full md:w-auto">
          <span className="text-custom-accent">/ </span>Work History
        </h2>
        
        <div className="w-full space-y-12">
          {workHistory.map((experience, companyIndex) => (
            <motion.div
              key={experience.company}
              custom={companyIndex}
              variants={experienceVariants}
              className="bg-gray-700 bg-opacity-50 p-6 rounded-lg"
            >
              <div className="flex flex-col sm:flex-row sm:items-baseline justify-between mb-4">
                <h3 className="text-2xl font-bold text-custom-accent">
                  {experience.company}
                </h3>
                {experience.location && (
                  <span className="text-gray-400 text-sm">{experience.location}</span>
                )}
              </div>
              
              <div className="space-y-6">
                {experience.positions.map((position, positionIndex) => {
                  const index = `${companyIndex}-${positionIndex}`;
                  return (
                    <motion.div
                      key={position.title}
                      custom={companyIndex + positionIndex}
                      variants={experienceVariants}
                      className="pl-4 border-l-2 border-custom-accent"
                    >
                      <div 
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 cursor-pointer"
                        onClick={() => toggleAccordion(index)}
                      >
                        <h4 className="text-xl font-semibold">{position.title}</h4>
                        <div className="flex items-center">
                          <span className="text-gray-400 text-sm mr-2">{position.period}</span>
                          <motion.svg
                            className="w-5 h-5 text-custom-accent"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            animate={{ rotate: openIndex === index ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </motion.svg>
                        </div>
                      </div>
                      <AnimatePresence>
                        {openIndex === index && (
                          <motion.div
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={accordionVariants}
                            className="overflow-hidden"
                          >
                            {Array.isArray(position.description) ? (
                              <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
                                {position.description.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-gray-300 mb-4">{position.description}</p>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {position.technologies.map((tech, techIndex) => (
                                <span
                                  key={tech}
                                  className="px-3 py-1 bg-gray-600 rounded-full text-sm"
                                >
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
} 